import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, MapPin, Phone, User } from 'lucide-react';

import { useClient } from '@/lib/clients-query';
import { useUploadPdfReport, useReports, useDiff } from '@/lib/reports-query';
import type { ChangeType, DiffResult } from '@/lib/reports.types';
import {
  useCreditItems,
  useDisputeRounds,
  useCreateDisputeRound,
  getLetterUrl,
} from '@/lib/disputes-query';
import type { DisputeRound as DisputeRoundType } from '@/lib/disputes.types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

const TABS = ['Overview', 'Reports', 'Diff', 'Disputes'] as const;
type TabKey = (typeof TABS)[number];

/**
 * /clients/:id — ficha del cliente con tabs (Overview / Reports / Diff /
 * Disputes). Reports se habilita en Hito 3; el resto llega en los próximos.
 */
export function ClientDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id!;
  const q = useClient(id);
  const [tab, setTab] = useState<TabKey>('Overview');

  return (
    <section className="mx-auto max-w-4xl px-6 py-10 space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/clients" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4 mr-1" /> Back to clients
        </Link>
      </div>

        {q.isLoading && (
          <div className="py-10 text-muted-foreground text-center">
            <Loader2 className="inline size-5 animate-spin mr-2" />
            Loading client…
          </div>
        )}
        {q.isError && (
          <div className="py-10 text-destructive text-center">
            Client not found or you don't have access.
          </div>
        )}
        {q.data && (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">{q.data.fullName}</h1>
                <p className="text-sm text-muted-foreground">
                  Created {formatDate(q.data.createdAt)} · ID{' '}
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{q.data.id}</code>
                </p>
              </div>
              <Badge variant={q.data.intakeCompleted ? 'default' : 'muted'}>
                {q.data.intakeCompleted ? 'intake completed' : 'intake pending'}
              </Badge>
            </div>

            {/* Tabs */}
            <nav className="flex border-b">
              {TABS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    t === tab
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t}
                </button>
              ))}
            </nav>

            {tab === 'Overview' && <OverviewTab data={q.data} />}
            {tab === 'Reports' && <ReportsTab clientId={q.data.id} />}
            {tab === 'Diff' && <DiffTab clientId={q.data.id} />}
            {tab === 'Disputes' && <DisputesTab clientId={q.data.id} />}
          </>
        )}
    </section>
  );
}

function OverviewTab({
  data,
}: {
  data: {
    fullName: string;
    ssnLast4: string;
    dob: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string | null;
    email: string | null;
    lastReport: unknown;
  };
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="size-4" /> Identity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Row label="Full name" value={data.fullName} />
          <Row label="SSN last-4" value={`•••${data.ssnLast4.slice(-1)}`} />
          <Row label="Date of birth" value={data.dob} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="size-4" /> Address
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Row label="Street" value={data.address} />
          <Row label="City" value={data.city} />
          <Row label="State / ZIP" value={`${data.state} · ${data.zip}`} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="size-4" /> Contact
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Row label="Phone" value={data.phone ?? '—'} />
          <Row
            label="Email"
            value={
              data.email ? (
                <a href={`mailto:${data.email}`} className="underline inline-flex items-center gap-1">
                  <Mail className="size-3.5" />
                  {data.email}
                </a>
              ) : (
                '—'
              )
            }
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Latest credit report</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {data.lastReport
            ? 'Report summary will appear here.'
            : 'No credit reports uploaded yet.'}
        </CardContent>
      </Card>
    </div>
  );
}

function ReportsTab({ clientId }: { clientId: string }) {
  const reports = useReports(clientId);
  const uploadPdf = useUploadPdfReport(clientId);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    if (selected && selected.type !== 'application/pdf') {
      setError('Only PDF files are allowed.');
      setFile(null);
      return;
    }
    setError(null);
    setFile(selected);
  };

  const submit = async (): Promise<void> => {
    if (!file) return;
    setError(null);
    setSuccess(null);
    try {
      const r = await uploadPdf.mutateAsync(file);
      setSuccess(`Report ingested — ${r.items ?? 0} items${r.id ? ` (id=${r.id.slice(0, 8)}…)` : ''}`);
      setFile(null);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (e as { message?: string })?.message
        || 'Failed to ingest report.';
      setError(msg);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ingest new monthly report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reportPdf">Upload credit report PDF</Label>
            <input
              id="reportPdf"
              type="file"
              accept="application/pdf"
              onChange={onFileChange}
              className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
            />
            <p className="text-xs text-muted-foreground">
              Accepted: PDF credit reports from Experian, Equifax, or TransUnion. Max 50 MB.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={submit}
              disabled={uploadPdf.isPending || !file}
            >
              {uploadPdf.isPending && <Loader2 className="size-4 mr-1 animate-spin" />}
              {uploadPdf.isPending ? 'Parsing & ingesting…' : 'Ingest report'}
            </Button>
            {success && <p className="text-sm text-green-600 dark:text-green-400">{success}</p>}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Report history</CardTitle>
        </CardHeader>
        <CardContent>
          {reports.isLoading ? (
            <p className="text-sm text-muted-foreground">
              <Loader2 className="inline size-4 animate-spin mr-2" />
              Loading reports…
            </p>
          ) : reports.isError ? (
            <p className="text-sm text-destructive">Failed to load reports.</p>
          ) : !reports.data || reports.data.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              No reports uploaded yet. Upload a PDF credit report above to start tracking this
              client's credit history.
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bureau</TableHead>
                    <TableHead>Report date</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Has diff</TableHead>
                    <TableHead>Uploaded</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.data.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="uppercase">{r.bureau}</TableCell>
                      <TableCell>{r.reportDate}</TableCell>
                      <TableCell>{r.score ?? '—'}</TableCell>
                      <TableCell>
                        {r.previousReportId ? (
                          <Badge variant="secondary">Diff available</Badge>
                        ) : (
                          <Badge variant="muted">Baseline</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(r.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DiffTab({ clientId }: { clientId: string }) {
  const reports = useReports(clientId);
  const [selectedId, setSelectedId] = useState<string>('');
  const diff = useDiff(selectedId || null);

  const nonBaseline = (reports.data ?? []).filter((r) => r.previousReportId);
  const selected = (reports.data ?? []).find((r) => r.id === selectedId);
  const isBaseline = selected && !selected.previousReportId;

  return (
    <div className="space-y-6">
      {/* Report selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Select a report to compare</CardTitle>
        </CardHeader>
        <CardContent>
          {reports.isLoading ? (
            <p className="text-sm text-muted-foreground">
              <Loader2 className="inline size-4 animate-spin mr-2" />
              Loading reports…
            </p>
          ) : !reports.data || reports.data.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No reports available. Ingest at least two reports to see diffs.
            </p>
          ) : (
            <div className="space-y-3">
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">— Select a report —</option>
                {reports.data.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.bureau.toUpperCase()} · {r.reportDate} · Score {r.score ?? '—'}
                    {r.previousReportId ? '' : '  (baseline)'}
                  </option>
                ))}
              </select>

              {!selectedId && (
                <p className="text-xs text-muted-foreground">
                  Showing the first {nonBaseline.length > 0 ? `${nonBaseline.length} report(s) with a diff available` : '0 reports with diffs'}. Reports
                  without a previous report (baseline) show &quot;no diff.&quot;
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diff result */}
      {selectedId && diff.isLoading && (
        <p className="text-sm text-muted-foreground text-center py-4">
          <Loader2 className="inline size-4 animate-spin mr-2" />
          Computing diff…
        </p>
      )}
      {selectedId && diff.isError && (
        <p className="text-sm text-destructive text-center py-4">
          Failed to load diff for the selected report.
        </p>
      )}
      {selectedId && isBaseline && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            <p className="font-medium">Baseline report</p>
            <p className="mt-1">
              This is the first report for this bureau. There is no previous report to compare
              against — all items are considered &quot;new.&quot;
            </p>
          </CardContent>
        </Card>
      )}
      {selectedId && diff.data && !isBaseline && (
        <DiffDisplay diff={diff.data} />
      )}
    </div>
  );
}

function DiffDisplay({ diff: d }: { diff: DiffResult }) {
  const isPos = d.scoreDelta >= 0;

  return (
    <>
      {/* Score delta card */}
      <Card className={isPos ? 'border-green-500/40' : 'border-red-500/40'}>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Score change</span>
            <span
              className={`text-2xl font-bold ${isPos ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}
            >
              {isPos ? '+' : ''}{d.scoreDelta}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <DiffCountBadge changeType="new" count={d.counts.new} />
          <DiffCountBadge changeType="stable" count={d.counts.stable} />
          <DiffCountBadge changeType="updated" count={d.counts.updated} />
          <DiffCountBadge changeType="deleted" count={d.counts.deleted} />
          <DiffCountBadge changeType="reinserted" count={d.counts.reinserted} />
        </CardContent>
      </Card>

      {/* Reinsertion alert */}
      {d.hasReinsertions && (
        <Card className="border-purple-500/40 bg-purple-50 dark:bg-purple-950/20">
          <CardContent className="py-4 text-sm flex items-center gap-2">
            <span className="text-purple-600 dark:text-purple-400 font-semibold">
              ⚠ Reinsertion detected
            </span>
            <span className="text-muted-foreground">
              One or more previously deleted items have reappeared on this report. Review
              before proceeding with disputes.
            </span>
          </CardContent>
        </Card>
      )}

      {/* Items table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Item changes</CardTitle>
        </CardHeader>
        <CardContent>
          {d.items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No items changed in this report.
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Change</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Creditor</TableHead>
                    <TableHead>Hash</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.items.map((item) => (
                    <TableRow key={item.hash}>
                      <TableCell>
                        <DiffBadge changeType={item.changeType} />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{item.entityIds.masterId.slice(0, 8)}…</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{item.entityIds.snapshotId.slice(0, 8)}…</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {item.hash.slice(0, 12)}…
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

const LABELS: Record<ChangeType, string> = {
  new: 'New',
  stable: 'Stable',
  updated: 'Updated',
  deleted: 'Deleted',
  reinserted: 'Reinserted',
};

const BADGE_STYLES: Record<ChangeType, string> = {
  new: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-300 dark:border-green-700',
  stable: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 border-gray-300 dark:border-gray-600',
  updated: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700',
  deleted: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-300 dark:border-red-700',
  reinserted: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 border-purple-300 dark:border-purple-700',
};

function DiffBadge({ changeType }: { changeType: ChangeType }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${BADGE_STYLES[changeType]}`}
    >
      {LABELS[changeType]}
    </span>
  );
}

function DiffCountBadge({ changeType, count }: { changeType: ChangeType; count: number }) {
  if (count === 0) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${BADGE_STYLES[changeType]}`}
    >
      {count} {LABELS[changeType]}
    </span>
  );
}

/* ───────────── Disputes tab ───────────── */

function DisputesTab({ clientId }: { clientId: string }) {
  const rounds = useDisputeRounds(clientId);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);

  const latestRoundNum = (rounds.data ?? []).reduce(
    (m, r) => Math.max(m, r.roundNumber),
    0,
  );

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {rounds.data?.length
            ? `${rounds.data.length} round(s) · latest #${latestRoundNum}`
            : 'No dispute rounds yet.'}
        </p>
        <Button type="button" size="sm" onClick={() => setShowNew((x) => !x)}>
          {showNew ? 'Cancel' : 'New round'}
        </Button>
      </div>

      {/* New round form */}
      {showNew && (
        <NewRoundForm
          clientId={clientId}
          onDone={() => setShowNew(false)}
        />
      )}

      {/* Rounds list */}
      {rounds.isLoading && (
        <p className="text-sm text-muted-foreground text-center py-4">
          <Loader2 className="inline size-4 animate-spin mr-2" />
          Loading rounds…
        </p>
      )}
      {rounds.isError && (
        <p className="text-sm text-destructive text-center py-4">
          Failed to load dispute rounds.
        </p>
      )}
      {rounds.data && rounds.data.length === 0 && !showNew && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            <p className="font-medium">No disputes yet</p>
            <p className="mt-1">Click &quot;New round&quot; above to start disputing items for this client.</p>
          </CardContent>
        </Card>
      )}

      {rounds.data && rounds.data.length > 0 && (
        <div className="space-y-3">
          {rounds.data.map((round) => (
            <RoundCard
              key={round.id}
              round={round}
              expanded={expanded === round.id}
              onToggle={() =>
                setExpanded((prev) => (prev === round.id ? null : round.id))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NewRoundForm({
  clientId,
  onDone,
}: {
  clientId: string;
  onDone: () => void;
}) {
  const items = useCreditItems(clientId);
  const create = useCreateDisputeRound(clientId);
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const submit = async (): Promise<void> => {
    if (selected.length === 0 || reason.length < 10) return;
    await create.mutateAsync({
      clientCreditItemIds: selected,
      disputeReason: reason,
      notes: notes || undefined,
    });
    setSelected([]);
    setReason('');
    setNotes('');
    onDone();
  };

  return (
    <Card className="border-primary/30">
      <CardHeader>
        <CardTitle className="text-base">New dispute round</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Credit items list */}
        <div>
          <Label className="text-sm font-medium mb-1 block">
            Select items to dispute
          </Label>
          {items.isLoading && (
            <p className="text-xs text-muted-foreground">
              <Loader2 className="inline size-3 animate-spin mr-1" />
              Loading items…
            </p>
          )}
          {items.isError && (
            <p className="text-xs text-destructive">Failed to load items.</p>
          )}
          {items.data && items.data.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No credit items found for this client.
            </p>
          )}
          {items.data && items.data.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-md border divide-y text-sm">
              {items.data.map((item) => {
                const alreadyDisputed =
                  item.currentDisputeStatus !== 'not_disputed' &&
                  item.currentDisputeStatus !== 'reinserted';

                return (
                  <label
                    key={item.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors ${
                      alreadyDisputed ? 'opacity-50 pointer-events-none' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(item.id)}
                      disabled={alreadyDisputed}
                      onChange={() => toggle(item.id)}
                      className="rounded border-input"
                    />
                    <span className="flex-1">
                      <span className="font-medium">{item.creditorName}</span>
                      <span className="text-muted-foreground ml-2">
                        {item.accountNumber} · {item.bureau}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground uppercase">
                      {item.currentDisputeStatus.replace(/_/g, ' ')}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {selected.length} item(s) selected.
          </p>
        </div>

        {/* Reason */}
        <div>
          <Label htmlFor="disputeReason" className="text-sm font-medium">
            Dispute reason
          </Label>
          <textarea
            id="disputeReason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this item is inaccurate… (min 10 chars)"
            rows={3}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        {/* Notes */}
        <div>
          <Label htmlFor="disputeNotes" className="text-sm font-medium">
            Internal notes (optional)
          </Label>
          <input
            id="disputeNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any internal notes for this round"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        {/* Submit */}
        <div className="flex items-center gap-3">
          <Button
            type="button"
            disabled={selected.length === 0 || reason.length < 10 || create.isPending}
            onClick={submit}
          >
            {create.isPending && <Loader2 className="size-4 mr-1 animate-spin" />}
            {create.isPending
              ? 'Creating…'
              : `Create round (${selected.length} item(s))`}
          </Button>
          {create.isError && (
            <p className="text-sm text-destructive">
              {(create.error as { message?: string })?.message || 'Failed.'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function RoundCard({
  round,
  expanded,
  onToggle,
}: {
  round: DisputeRoundType;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <span className="font-semibold text-sm">Round #{round.roundNumber}</span>
            <StatusBadge status={round.status} />
            <span className="text-xs text-muted-foreground">
              {round.items.length} item(s)
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            {expanded ? '▲' : '▼'}
          </span>
        </button>
      </CardHeader>
      {expanded && (
        <CardContent className="px-4 pb-4 pt-0 space-y-3">
          {round.notes && (
            <p className="text-xs text-muted-foreground italic">
              {round.notes}
            </p>
          )}

          {round.items.length === 0 ? (
            <p className="text-xs text-muted-foreground">No items in this round.</p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Creditor</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {round.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <DisputeItemBadge status={item.status} />
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.creditItem?.creditorName ?? '—'}
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {item.creditItem?.accountNumber ?? item.clientCreditItemId.slice(0, 8)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                        {item.disputeReason}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <a
              href={getLetterUrl(round.id)}
              download
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <svg className="size-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0-3-3m3 3 3-3m2 8H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
              </svg>
              Download PDF
            </a>
            {round.status === 'draft' && (
              <p className="text-xs text-muted-foreground">
                Clicking Download will generate the letter and move this round to SENT.
              </p>
            )}
            {round.status === 'sent' && (
              <p className="text-xs text-muted-foreground">
                Letter generated. Re-download if needed.
              </p>
            )}
            {round.status === 'completed' && (
              <p className="text-xs text-green-600 dark:text-green-400">
                Round completed — all items resolved.
              </p>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

const ROUND_BADGE: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${ROUND_BADGE[status] || ''}`}
    >
      {status.toUpperCase()}
    </span>
  );
}

const ITEM_BADGE: Record<string, string> = {
  not_disputed: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  disputed_r1: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  disputed_r2: 'bg-orange-200 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400',
  disputed_r3: 'bg-orange-300 text-orange-900 dark:bg-orange-900/70 dark:text-orange-300',
  verified: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  deleted: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  updated: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  reinserted: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
};

function DisputeItemBadge({ status }: { status: string }) {
  const label = status.replace(/_/g, ' ');
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${ITEM_BADGE[status] || ''}`}
    >
      {label}
    </span>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}